'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import useFavorites from '@/hooks/useFavorites';
import { Button } from '@/components/ui/Button';
import { sendGoogleAdsConversion } from '@/lib/google-ads-conversion';

// WhatsApp iconunu ekleyelim
const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 6.628 5.373 12 12 12s12-5.373 12-12c0-6.628-5.373-12-12-12zM5.894 16.801c-.773 1.356-1.334 2.836-1.449 3.033l-1.378-3.603c-.428-.768-.676-1.639-.676-2.564 0-.483.065-.954.19-1.408 2.569-1.537 4.835-3.62 4.835-3.62s.708.44 1.411.915c-.647.463-1.136.832-1.136.832-3.114 2.337-1.796 6.416-1.796 6.416zm9.678.648c-2.164-.624-3.971-2.053-5.067-3.947-1.096-1.893-1.363-4.144-.753-6.238.61-2.095 2.173-3.797 4.198-4.582 2.025-.785 4.286-.616 6.181.478 1.14.659 2.091 1.627 2.778 2.798-1.633.908-3.358 2.192-3.358 2.192s-.583-1.005-1.608-2.028c-1.025-1.023-2.489-1.678-4.011-1.676-1.521.002-2.984.661-4.005 1.685-1.022 1.025-1.585 2.491-1.583 4.012.002 1.521.661 2.984 1.685 4.006 1.024 1.022 2.49 1.585 4.01 1.583 1.52-.002 2.984-.661 4.005-1.685.218-.218.41-.455.58-.705-.195.127-2.855 3.013-7.052 4.107z"/>
  </svg>
);

const BulkQuotePage = () => {
  const { favorites } = useFavorites();
  const [mounted, setMounted] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    company: ''
  });
  /** Favori ürün id → adet (PDF ve teklif için) */
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  
  // WhatsApp iletişim numarası
  const whatsappNumber = '905323457227'; // Buraya gerçek WhatsApp numaranızı yazın (başında 90 ile)
  
  // Sayfanın client tarafında çalıştığından emin olalım
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setQuantities((prev) => {
      const next: Record<string, number> = {};
      for (const p of favorites) {
        const id = String(p.id);
        const existing = prev[id];
        next[id] =
          typeof existing === 'number' && existing >= 1 ? Math.min(999, existing) : 1;
      }
      return next;
    });
  }, [favorites]);

  // Resim hata kontrolü
  const handleImageError = (productId: string) => {
    setImageErrors(prev => ({
      ...prev,
      [productId]: true
    }));
  };

  // Fallback image URL
  const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23d72638' fill-opacity='0.15'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 20v-1.41l2.83-2.83 1.41 1.41L1.41 20H0zm20 0v-1.41l2.83-2.83 1.41 1.41L21.41 20H20zM0 0v1.41l2.83 2.83-1.41 1.41L0 1.41V0h1.41l2.83 2.83-1.41 1.41L0 4.24V2.83L2.83 0H0zm8.49 8.49l2.83-2.83 1.41 1.41-2.83 2.83-1.41-1.41zm12.02 12.02l2.83-2.83 1.41 1.41-2.83 2.83-1.41-1.41zm1.41-9.19l2.83 2.83-1.41 1.41-2.83-2.83 1.41-1.41zM10 20v-1.41l2.83-2.83 1.41 1.41L11.41 20H10zm-8.49 8.49l-2.83-2.83 1.41-1.41 2.83 2.83-1.41 1.41zm24.98-19.95L23.66 5.7l1.41-1.41 2.83 2.83-1.41 1.41zm-1.41 19.95l-2.83-2.83 1.41-1.41 2.83 2.83-1.41 1.41zm7.07-7.07l-2.83-2.83 1.41-1.41 2.83 2.83-1.41 1.41zm-9.9-9.9L18.42.62l1.41-1.41 2.83 2.83-1.41 1.41z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";

  // Form gönderme işlemi
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Form doğrulama
    if (!formData.name || !formData.email || !formData.phone) {
      setFormError('Lütfen tüm zorunlu alanları doldurun');
      return;
    }
    
    setIsSubmitting(true);
    setFormError('');
    
    try {
      // API'ye gönderilecek veriyi hazırla
      const requestData = {
        ...formData,
        products: favorites.map((product) => {
          const id = String(product.id);
          const q = quantities[id];
          const quantity =
            typeof q === 'number' && q >= 1 ? Math.min(999, Math.floor(q)) : 1;
          return {
            id: product.id,
            name: product.name,
            category: product.category,
            quantity,
          };
        }),
      };
      
      // API'ye POST isteği gönder
      const response = await fetch('/api/bulk-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      
      // Yanıtı işle
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Beklenmeyen bir hata oluştu');
      }
      
      // Başarılı yanıt
      setIsSuccess(true);
      sendGoogleAdsConversion();
      
      // Form başarıyla gönderildiğinde formu sıfırla
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        company: ''
      });
      
      // 5 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (error) {
      // Hata mesajını göster
      setFormError(error instanceof Error ? error.message : 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // WhatsApp ile paylaşım
  const handleWhatsAppShare = useCallback(() => {
    // Form doğrulama
    if (!formData.name || !formData.phone) {
      setFormError('Lütfen en azından ad ve telefon bilgilerinizi doldurun');
      return;
    }
    
    const origin = window.location.origin;
    const productsList = favorites
      .map((product, index) => {
        const id = String(product.id);
        const q = quantities[id];
        const adet =
          typeof q === 'number' && q >= 1 ? Math.min(999, Math.floor(q)) : 1;
        const line = `${index + 1}. ${product.name} — Adet: ${adet} (Kategori: ${product.category || 'Belirtilmemiş'})`;
        const url = product.slug ? `${origin}/urunler/${product.slug}` : null;
        return url ? `${line}\nÜrün sayfası: ${url}` : line;
      })
      .join('\n\n');
    
    // Mesaj içeriğini hazırla
    const message = `
*Toplu Fiyat Teklifi İsteği*
----------------------------
*İsim:* ${formData.name}
*Telefon:* ${formData.phone}
${formData.email ? `*Email:* ${formData.email}` : ''}
${formData.company ? `*Firma:* ${formData.company}` : ''}

*Ürün Listesi:*
${productsList}

${formData.message ? `*Adres bilgisi:* ${formData.message}` : ''}
----------------------------
`;
    
    // WhatsApp URL'ini oluştur
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Yeni pencerede WhatsApp'ı aç
    window.open(whatsappUrl, '_blank');
    
  }, [formData, favorites, quantities, whatsappNumber]);

  // Form alanlarını güncelleme
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Client tarafında çalışana kadar bir şey gösterme
  if (!mounted) {
    return null;
  }

  // Favori yoksa yönlendir
  if (favorites.length === 0) {
    return (
      <div className="min-h-screen py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <div className="mb-6">
            <svg 
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto text-gray-300" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={1.5} 
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Favorilerinizde Henüz Ürün Yok</h2>
          <p className="text-gray-500 mb-8">Toplu fiyat teklifi istemek için önce favori ürünler eklemelisiniz.</p>
          <Link href="/urunler">
            <Button 
              variant="outline"
              size="lg"
              className="font-medium hover:bg-primary/10"
            >
              Ürünleri Keşfedin
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen">
      {/* Sayfa Başlığı */}
      <div className="bg-gradient-to-r from-primary/10 to-primary-dark/10 py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Toplu Fiyat Teklifi</h1>
          <p className="mt-2 text-gray-600">Favori ürünleriniz için tek seferde fiyat teklifi alın</p>
        </div>
      </div>

      {/* İçerik */}
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Ürün Listesi */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Fiyat Teklifi İstenen Ürünler</h2>
              
              <div className="space-y-4">
                {favorites.map((product) => {
                  const pid = String(product.id);
                  const adet = quantities[pid] ?? 1;
                  return (
                    <div
                      key={product.id}
                      className="flex flex-wrap items-center gap-4 p-3 border border-gray-100 rounded-lg hover:bg-gray-50"
                    >
                      <div className="w-16 h-16 min-h-[64px] aspect-square relative flex-shrink-0 bg-gray-50 rounded overflow-hidden">
                        <Image
                          src={imageErrors[product.id] ? fallbackImage : product.image}
                          alt={product.name?.trim() || 'Filiz Kozmetik ürün görseli'}
                          fill
                          className="object-cover"
                          sizes="64px"
                          onError={() => handleImageError(product.id)}
                        />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h3 className="font-medium text-gray-800">{product.name}</h3>
                        <p className="text-sm text-gray-500">{product.category}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <label htmlFor={`qty-${pid}`} className="text-sm text-gray-600 whitespace-nowrap">
                          Adet
                        </label>
                        <input
                          id={`qty-${pid}`}
                          type="number"
                          min={1}
                          max={999}
                          value={adet}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            const next =
                              !Number.isFinite(v) || v < 1
                                ? 1
                                : Math.min(999, v);
                            setQuantities((prev) => ({ ...prev, [pid]: next }));
                          }}
                          className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
                          aria-label={`${product.name} adet`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 flex justify-between items-center">
                <div className="text-sm text-gray-500">Toplam {favorites.length} ürün</div>
                <Link href="/favoriler" className="text-primary text-sm font-medium hover:underline">
                  Favorilerime Dön
                </Link>
              </div>
            </div>
          </div>
          
          {/* Teklif İstek Formu */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">İletişim Bilgileriniz</h2>
              
              {isSuccess && (
                <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md">
                  Teklif isteğiniz başarıyla gönderildi! En kısa sürede sizinle iletişime geçeceğiz.
                </div>
              )}
              
              {formError && (
                <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                  {formError}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Adınız Soyadınız *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="Adınız ve soyadınız"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      E-posta Adresiniz *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="örnek@gmail.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon Numaranız *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="05XX XXX XX XX"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                      Firma Adı
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="Firma adınız (opsiyonel)"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Adres bilgisi
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="Teslimat adresi: mahalle, sokak, ilçe, il, posta kodu (isteğe bağlı)"
                    />
                  </div>
                </div>
                
                <div className="mt-6 space-y-3">
                  <Button
                    type="submit"
                    className="w-full py-3 px-4 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors duration-300 flex items-center justify-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Gönderiliyor...
                      </>
                    ) : (
                      <>Fiyat Teklifi İste</>
                    )}
                  </Button>
                  
                  {/* WhatsApp Butonu */}
                  <button
                    type="button"
                    onClick={handleWhatsAppShare}
                    className="w-full py-3 px-4 bg-[#25D366] text-white rounded-md hover:bg-[#128C7E] transition-colors duration-300 flex items-center justify-center"
                  >
                    <WhatsAppIcon />
                    <span className="ml-2">WhatsApp ile Teklif İste</span>
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center mt-2">
                    WhatsApp butonu ile bilgileriniz ve seçtiğiniz ürünler doğrudan WhatsApp üzerinden iletilecektir.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default BulkQuotePage; 